import { cn } from '@/utils/ui/styles'

import { Skeleton } from '@/components/ui/skeleton'

/**
 * Composable table skeleton components for loading states
 *
 * Usage:
 * @example
 * // Full table skeleton with toolbar
 * <TableToolbarSkeleton>
 *   <TableSearchSkeleton />
 *   <TableFiltersSkeleton />
 *   <TableLastUpdatedSkeleton />
 * </TableToolbarSkeleton>
 * <TableRowsSkeleton rows={10} />
 *
 * @example
 * // Shorthand for common patterns
 * <TableFullSkeleton rows={10} withFilters />
 */

type TableToolbarSkeletonProps = {
	className?: string
	withFilters?: boolean
	withLastUpdated?: boolean
}

/**
 * Skeleton wrapper for table toolbar
 */
export function TableToolbarSkeleton({
	className,
	withFilters = false,
	withLastUpdated = false,
}: TableToolbarSkeletonProps) {
	return (
		<div
			className={cn(
				'flex flex-col items-center justify-between gap-4 sm:flex-row',
				className,
			)}
		>
			<div className="flex w-full flex-col gap-2">
				<div className="flex w-full items-center gap-2">
					{withFilters && <TableFiltersSkeleton />}
					<TableSearchSkeleton />
				</div>
			</div>
			{withLastUpdated && <TableLastUpdatedSkeleton />}
		</div>
	)
}

type TableSearchSkeletonProps = {
	className?: string
}

/**
 * Skeleton for search input
 */
export function TableSearchSkeleton({ className }: TableSearchSkeletonProps) {
	return <Skeleton className={cn('h-12 w-full', className)} />
}

type TableFiltersSkeletonProps = {
	className?: string
}

/**
 * Skeleton for filters button
 */
export function TableFiltersSkeleton({ className }: TableFiltersSkeletonProps) {
	return <Skeleton className={cn('h-12 w-24 shrink-0', className)} />
}

type TableLastUpdatedSkeletonProps = {
	className?: string
}

/**
 * Skeleton for last updated + refresh button
 */
export function TableLastUpdatedSkeleton({
	className,
}: TableLastUpdatedSkeletonProps) {
	return (
		<div
			className={cn(
				'flex w-90 shrink-0 grow-0 items-center justify-end gap-4',
				className,
			)}
		>
			<Skeleton className="h-6 w-72" />
			<Skeleton className="size-8 shrink-0 grow-0" />
		</div>
	)
}

type TableRowsSkeletonProps = {
	rows?: number
	className?: string
}

/**
 * Skeleton for table rows
 */
export function TableRowsSkeleton({
	rows = 10,
	className,
}: TableRowsSkeletonProps) {
	return (
		<div
			className={cn(
				'flex w-full flex-col items-start justify-start gap-2',
				className,
			)}
		>
			{/* Header row */}
			<Skeleton className="h-10 w-full rounded-none" />

			{/* Data rows */}
			{Array.from({ length: rows }).map((_, i) => (
				<Skeleton key={i} className="h-12 w-full rounded-none" />
			))}
		</div>
	)
}

type TableFullSkeletonProps = {
	rows?: number
	withFilters?: boolean
	withLastUpdated?: boolean
	className?: string
}

/**
 * Full table skeleton with toolbar and rows
 *
 * @example
 * <TableFullSkeleton rows={10} withFilters withLastUpdated />
 */
export function TableFullSkeleton({
	rows = 10,
	withFilters = false,
	withLastUpdated = true,
	className,
}: TableFullSkeletonProps) {
	return (
		<div className={cn('flex flex-col gap-4', className)}>
			<TableToolbarSkeleton
				withFilters={withFilters}
				withLastUpdated={withLastUpdated}
			/>

			<TableRowsSkeleton rows={rows} />
		</div>
	)
}
